/**
 * Audit Service
 * Handles automatic logging of user actions for compliance and security monitoring
 */

import { userManagementService } from './user-management-service';
import { EnhancedUser } from './rbac-types';

export interface AuditContext {
  user_id?: string;
  user_email?: string;
  org_id?: string;
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
}

export interface AuditAction {
  action: string;
  resource_type: string;
  resource_id?: string;
  details?: Record<string, any>;
  context?: AuditContext;
  success?: boolean;
  error_message?: string;
}

export class AuditService {
  private static instance: AuditService;
  private context: AuditContext = {};
  private batchQueue: AuditAction[] = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly BATCH_DELAY = 5000; // 5 seconds

  private constructor() {
    // Initialize with browser context if available
    if (typeof window !== 'undefined') {
      this.context = {
        ip_address: 'client', // In production, this would be set by the server
        user_agent: navigator.userAgent
      };
    }
  }

  static getInstance(): AuditService {
    if (!AuditService.instance) {
      AuditService.instance = new AuditService();
    }
    return AuditService.instance;
  }

  /**
   * Set the current audit context (user, organization, session)
   */
  setContext(context: Partial<AuditContext>): void {
    this.context = { ...this.context, ...context };
  }

  /**
   * Update user context from authenticated user
   */
  setUserContext(user: EnhancedUser, orgId?: string): void {
    this.setContext({
      user_id: user.user_id,
      user_email: user.email,
      org_id: orgId || user.active_org_id
    });
  }

  /**
   * Clear user context (on logout)
   */
  clearUserContext(): void {
    const { ip_address, user_agent } = this.context;
    this.context = { ip_address, user_agent };
  }

  /**
   * Log a user action
   */
  async logAction(action: Omit<AuditAction, 'context'>): Promise<void> {
    const auditAction: AuditAction = {
      ...action,
      context: this.context,
      success: action.success !== false // Default to true unless explicitly false
    };

    // Add to batch queue
    this.batchQueue.push(auditAction);

    // Process batch if it reaches the size limit
    if (this.batchQueue.length >= this.BATCH_SIZE) {
      await this.processBatch();
    } else {
      // Schedule batch processing if not already scheduled
      if (!this.batchTimeout) {
        this.batchTimeout = setTimeout(() => {
          this.processBatch();
        }, this.BATCH_DELAY);
      }
    }

    // Also log immediately for critical security events
    if (this.isCriticalSecurityEvent(action.action)) {
      await this.logImmediately(auditAction);
    }
  }

  /**
   * Process batched audit actions
   */
  private async processBatch(): Promise<void> {
    if (this.batchQueue.length === 0) {
      return;
    }

    const actionsToProcess = [...this.batchQueue];
    this.batchQueue = [];

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    // Process each action in the batch
    for (const action of actionsToProcess) {
      try {
        await userManagementService.logUserAction(
          action.action,
          action.resource_type,
          action.resource_id,
          {
            ...action.details,
            context: action.context,
            success: action.success,
            error_message: action.error_message
          }
        );
      } catch (error) {
        console.error('Failed to log audit action:', error, action);
      }
    }
  }

  /**
   * Log action immediately (for critical events)
   */
  private async logImmediately(action: AuditAction): Promise<void> {
    try {
      await userManagementService.logUserAction(
        action.action,
        action.resource_type,
        action.resource_id,
        {
          ...action.details,
          context: action.context,
          success: action.success,
          error_message: action.error_message
        }
      );
    } catch (error) {
      console.error('Failed to log critical audit action:', error, action);
    }
  }

  /**
   * Check if an action is a critical security event
   */
  private isCriticalSecurityEvent(action: string): boolean {
    const criticalEvents = [
      'security:permission_denied',
      'security:login_failed',
      'security:password_reset',
      'security:mfa_failed',
      'security:suspicious_activity',
      'user:delete',
      'organization:delete'
    ];

    return criticalEvents.includes(action);
  }

  /**
   * Flush any pending audit actions
   */
  async flush(): Promise<void> {
    await this.processBatch();
  }

  // Convenience methods for common audit actions

  async logUserLogin(userId: string, success: boolean, details?: Record<string, any>): Promise<void> {
    await this.logAction({
      action: success ? 'user:login' : 'security:login_failed',
      resource_type: 'user',
      resource_id: userId,
      details,
      success
    });
  }

  async logUserLogout(userId: string): Promise<void> {
    await this.logAction({
      action: 'user:logout',
      resource_type: 'user',
      resource_id: userId
    });
  }

  async logUserCreation(userId: string, details?: Record<string, any>): Promise<void> {
    await this.logAction({
      action: 'user:create',
      resource_type: 'user',
      resource_id: userId,
      details
    });
  }

  async logUserUpdate(userId: string, details?: Record<string, any>): Promise<void> {
    await this.logAction({
      action: 'user:update',
      resource_type: 'user',
      resource_id: userId,
      details
    });
  }

  async logUserSuspension(userId: string, reason: string): Promise<void> {
    await this.logAction({
      action: 'user:suspend',
      resource_type: 'user',
      resource_id: userId,
      details: { reason }
    });
  }

  async logUserActivation(userId: string): Promise<void> {
    await this.logAction({
      action: 'user:activate',
      resource_type: 'user',
      resource_id: userId
    });
  }

  async logPermissionDenied(resource: string, permission: string, details?: Record<string, any>): Promise<void> {
    await this.logAction({
      action: 'security:permission_denied',
      resource_type: resource,
      details: { permission, ...details },
      success: false
    });
  }

  async logRoleChange(userId: string, oldRole: string, newRole: string): Promise<void> {
    await this.logAction({
      action: 'user:role_change',
      resource_type: 'user',
      resource_id: userId,
      details: { old_role: oldRole, new_role: newRole }
    });
  }

  async logInvitationSent(email: string, role: string): Promise<void> {
    await this.logAction({
      action: 'invitation:sent',
      resource_type: 'invitation',
      details: { email, role }
    });
  }

  async logInvitationAccepted(invitationId: string, userId: string): Promise<void> {
    await this.logAction({
      action: 'invitation:accepted',
      resource_type: 'invitation',
      resource_id: invitationId,
      details: { user_id: userId }
    });
  }

  async logExperimentAction(action: string, experimentId: string, details?: Record<string, any>): Promise<void> {
    await this.logAction({
      action: `experiment:${action}`,
      resource_type: 'experiment',
      resource_id: experimentId,
      details
    });
  }

  async logDeploymentAction(action: string, deploymentId: string, details?: Record<string, any>): Promise<void> {
    await this.logAction({
      action: `deployment:${action}`,
      resource_type: 'deployment',
      resource_id: deploymentId,
      details
    });
  }

  async logDataAccess(resourceType: string, resourceId: string, details?: Record<string, any>): Promise<void> {
    await this.logAction({
      action: 'data:access',
      resource_type: resourceType,
      resource_id: resourceId,
      details
    });
  }

  async logConfigurationChange(configType: string, details?: Record<string, any>): Promise<void> {
    await this.logAction({
      action: 'config:change',
      resource_type: 'configuration',
      resource_id: configType,
      details
    });
  }
}

// Singleton instance
export const auditService = AuditService.getInstance();

// React hook for easy audit logging in components
export const useAuditLogger = () => {
  return auditService;
};

export default auditService;